import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import AgentRelationshipsAPI from 'dashboard/api/agentRelationships';

const FEATURE_FLAG = 'shopwindows_all_agents_enabled';
const STORAGE_KEY = 'lais_shopwindow_feature_cache_v1';

const normalize = value =>
  String(value || '')
    .trim()
    .toLowerCase();

const truthy = value =>
  value === true || value === 1 || value === '1' || value === 'true';

const readBooleanFlag = (payload, snakeKey, camelKey) =>
  truthy(payload?.[snakeKey] ?? payload?.[camelKey]);

const agentIdentifiers = agentProfile =>
  [
    agentProfile?.agent_code,
    agentProfile?.agentCode,
    agentProfile?.agent_email,
    agentProfile?.agentEmail,
    agentProfile?.email,
    agentProfile?.agent_name,
    agentProfile?.agentName,
    agentProfile?.name,
  ]
    .map(normalize)
    .filter(Boolean);

const entryAssigneeIdentifiers = entry => {
  const assignee = entry?.assignee || {};
  const metadata = entry?.metadata || {};

  return [
    assignee.email,
    assignee.name,
    entry?.assignee_email,
    entry?.assigneeEmail,
    entry?.assignee_name,
    entry?.assigneeName,
    metadata.agent_code,
    metadata.agentCode,
    metadata.identifier,
  ]
    .map(normalize)
    .filter(Boolean);
};

const isEntryAssignedToAgent = (entry, agentProfile = {}) => {
  const agentValues = agentIdentifiers(agentProfile);
  if (!agentValues.length) return false;

  return entryAssigneeIdentifiers(entry).some(value =>
    agentValues.includes(value)
  );
};

const firstExternalHolderName = (entries, agentProfile = {}) => {
  const entry = entries.find(
    item => !isEntryAssignedToAgent(item, agentProfile)
  );
  return (
    entry?.assignee?.name ||
    entry?.assignee_name ||
    entry?.assigneeName ||
    entry?.assignee?.email ||
    entry?.assignee_email ||
    entry?.assigneeEmail ||
    ''
  );
};

export function useShopWindowAccess() {
  const store = useStore();
  const accountId = computed(() => store.getters.getCurrentAccountId);

  const featureEnabled = ref(false);
  const keysWithAgent = ref(false);
  const keyHolderValidationEnabled = ref(false);
  const keyHolderEntries = ref([]);
  const blockedByKeyHolder = ref(false);
  const blockedHolderName = ref('');
  const checking = ref(false);
  const lastError = ref(null);

  const readCache = () => {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  };

  const writeCache = payload => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (error) {
      // ignore
    }
  };

  const ensureFeatureFlag = async () => {
    const cached = readCache();
    if (cached && cached.expiresAt > Date.now()) {
      featureEnabled.value = cached.enabled;
      return cached.enabled;
    }

    const enabled =
      Boolean(
        store.getters['accounts/isFeatureEnabledonAccount']?.(
          accountId.value,
          FEATURE_FLAG
        )
      ) ||
      Boolean(
        store.getters['accounts/getAccountFeatures']?.(accountId.value)?.[
          FEATURE_FLAG
        ]
      );

    featureEnabled.value = enabled;
    writeCache({ enabled, expiresAt: Date.now() + 5 * 60 * 1000 });
    return enabled;
  };

  const checkEntryAccess = async (imovelId, agentProfile = {}) => {
    if (!imovelId) {
      keysWithAgent.value = false;
      keyHolderValidationEnabled.value = false;
      keyHolderEntries.value = [];
      blockedByKeyHolder.value = false;
      blockedHolderName.value = '';
      return {
        keysWithAgent: false,
        keyHolderValidationEnabled: false,
        entries: [],
        blockedByKeyHolder: false,
        blockedHolderName: '',
      };
    }

    checking.value = true;
    lastError.value = null;
    try {
      const { data } = await AgentRelationshipsAPI.keysWithAgent(imovelId);
      const entries = Array.isArray(data?.entries) ? data.entries : [];
      const hasCurrentAgentEntry = entries.some(entry =>
        isEntryAssignedToAgent(entry, agentProfile)
      );
      const holderName = firstExternalHolderName(entries, agentProfile);
      const responseKeysWithAgent = readBooleanFlag(
        data,
        'keys_with_agent',
        'keysWithAgent'
      );
      const responseValidationEnabled = readBooleanFlag(
        data,
        'key_holder_validation_enabled',
        'keyHolderValidationEnabled'
      );
      const blocked = Boolean(
        responseValidationEnabled &&
          responseKeysWithAgent &&
          entries.length &&
          !hasCurrentAgentEntry
      );

      keysWithAgent.value = responseKeysWithAgent;
      keyHolderValidationEnabled.value = responseValidationEnabled;
      keyHolderEntries.value = entries;
      blockedByKeyHolder.value = blocked;
      blockedHolderName.value = holderName;

      return {
        keysWithAgent: keysWithAgent.value,
        keyHolderValidationEnabled: keyHolderValidationEnabled.value,
        entries,
        blockedByKeyHolder: blocked,
        blockedHolderName: holderName,
      };
    } catch (error) {
      lastError.value = error;
      keysWithAgent.value = false;
      keyHolderValidationEnabled.value = false;
      keyHolderEntries.value = [];
      blockedByKeyHolder.value = false;
      blockedHolderName.value = '';
      return {
        keysWithAgent: false,
        keyHolderValidationEnabled: false,
        entries: [],
        blockedByKeyHolder: false,
        blockedHolderName: '',
      };
    } finally {
      checking.value = false;
    }
  };

  const canCreateShopWindow = computed(
    () =>
      (featureEnabled.value || keysWithAgent.value) && !blockedByKeyHolder.value
  );

  return {
    featureEnabled,
    keysWithAgent,
    keyHolderValidationEnabled,
    keyHolderEntries,
    blockedByKeyHolder,
    blockedHolderName,
    checking,
    lastError,
    canCreateShopWindow,
    ensureFeatureFlag,
    checkEntryAccess,
    marketplaceUrl: '/lais/hub-corretor',
  };
}
